import merge from "lodash/merge";
import type React from "react";
import { useContext, useEffect, useState } from "react";
import { IntlProvider } from "react-intl";
import { errorI18n } from "../pages/Error/errorI18n";
import { isLocal } from "../utils/env";
import htmlHandler from "../utils/html-handler";
import { translateOutOfReactScopeMessages } from "../utils/internationalization";
import { configStore } from "./ConfigContext";
import { errorStore } from "./ErrorContext";

interface LangProviderProps {
  children: React.ReactNode;
}

const LangProvider = ({ children }: LangProviderProps) => {
  const { config } = useContext(configStore);
  const { setErrorInfo } = useContext(errorStore);
  const [messages, setMessages] = useState<any>();

  // Load i18n
  useEffect(() => {
    if (!config.lang) return;

    document.documentElement.setAttribute("lang", config.lang);

    (async () => {
      try {
        // Vite/Rollup expose a dynamically-imported JSON's contents under
        // `.default` (the message IDs contain "-", so they aren't valid named
        // exports). Webpack/CRA used to spread the JSON keys onto the module
        // namespace directly, so this previously read `baseMessages.<id>`
        // without `.default`. Unwrap `.default` or react-intl gets a
        // `{ default: {...} }` object, matches no IDs, and falls back to
        // `defaultLocale="eng"` (every non-English locale renders in English).
        const baseMessages = (await import(`../../i18n/${config.lang}.json`))
          .default;
        let variationMessages = {};
        try {
          let variationLanguageCode = config.declinaison.replace("/", "-");
          variationLanguageCode = variationLanguageCode.replace(
            "specimen-",
            "",
          );
          variationMessages = (
            await import(`../../i18n/${variationLanguageCode}.json`)
          ).default;
        } catch {
          // ignore
        }
        const messages = merge({}, baseMessages, variationMessages);
        setMessages(messages);
        translateOutOfReactScopeMessages(messages);
      } catch {
        setErrorInfo((curr) => ({
          ...curr,
          page: errorI18n.find((page) => page.code === "i18n"),
        }));
      }
    })();
  }, [config.lang, setErrorInfo, config.declinaison]);

  const handleError = isLocal(window.location.hostname) ? () => {} : undefined;

  return (
    <IntlProvider
      locale={config.lang ?? ""}
      defaultLocale="eng"
      messages={messages}
      onError={handleError}
      defaultRichTextElements={htmlHandler}
    >
      {children}
    </IntlProvider>
  );
};

export default LangProvider;
